import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../../services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Sidebar.css';

const Sidebar = ({ selectedProjectId, onSelectProject }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [newProjectName, setNewProjectName] = useState('');
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isProfileRoute = location.pathname === '/profile';
  const isAllGenerationsRoute = location.pathname === '/';
  const isMyGenerationsRoute = location.pathname === '/my-generations';
  const isFailedGenerationsRoute = location.pathname === '/failed';
  const isPromptLibraryRoute = location.pathname === '/prompt-library';
  const isDownscalerRoute = location.pathname === '/downscaler';
  const isProjectRoute = location.pathname.startsWith('/project/');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    try {
      await projectsAPI.create(newProjectName, null);
      setNewProjectName('');
      setShowNewProjectForm(false);
      loadProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await projectsAPI.delete(projectId);
      loadProjects();
      if (selectedProjectId === projectId) {
        onSelectProject(null);
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <h3>AI UGC</h3>
        {user?.username && (
          <button
            type="button"
            className={`profile-username ${isProfileRoute ? 'active' : ''}`}
            onClick={() => {
              onSelectProject(null);
              navigate('/profile');
            }}
            title="View profile"
          >
            @{user.username}
          </button>
        )}
      </div>

      {showNewProjectForm && (
        <form onSubmit={handleCreateProject} className="new-project-form">
          <input
            type="text"
            placeholder="Project name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            autoFocus
          />
          <div className="form-actions">
            <button type="submit" className="btn-sm btn-primary">Create</button>
            <button
              type="button"
              className="btn-sm btn-secondary"
              onClick={() => {
                setShowNewProjectForm(false);
                setNewProjectName('');
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="sidebar-menu">
        <div
          className={`menu-item ${isAllGenerationsRoute ? 'active' : ''}`}
          onClick={() => {
            onSelectProject(null);
            navigate('/');
          }}
        >
          All Generations
        </div>
        <div
          className={`menu-item ${isMyGenerationsRoute ? 'active' : ''}`}
          onClick={() => {
            onSelectProject(null);
            navigate('/my-generations');
          }}
        >
          My Generations
        </div>
        <div
          className={`menu-item ${isFailedGenerationsRoute ? 'active' : ''}`}
          onClick={() => {
            onSelectProject(null);
            navigate('/failed');
          }}
        >
          Failed Generations
        </div>
        <div
          className={`menu-item ${isPromptLibraryRoute ? 'active' : ''}`}
          onClick={() => {
            onSelectProject(null);
            navigate('/prompt-library');
          }}
        >
          Prompt Library
        </div>
      </div>

      <div className="sidebar-menu downscaler-section">
        <div
          className={`menu-item ${isDownscalerRoute ? 'active' : ''}`}
          onClick={() => {
            onSelectProject(null);
            navigate('/downscaler');
          }}
        >
          Downscaler
        </div>
      </div>

      <div className="projects-list">
        <div className="projects-list-header">
          <h4>Projects</h4>
          <button
            className="btn-icon"
            onClick={() => setShowNewProjectForm(!showNewProjectForm)}
            title="New Project"
          >
            +
          </button>
        </div>
        {projects.length === 0 ? (
          <p className="empty-message">No projects yet</p>
        ) : (
          projects.map((project) => (
            <div
              key={project.id}
              className={`project-item ${
                isProjectRoute && selectedProjectId === project.id ? 'active' : ''
              }`}
            >
              <span
                className="project-name"
                onClick={() => {
                  onSelectProject(project.id);
                  navigate(`/project/${project.id}`);
                }}
              >
                {project.name}
              </span>
              <button
                className="btn-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteProject(project.id);
                }}
                title="Delete project"
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Sidebar;
